import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Supplier {
  id: string
  name: string
  category: string
  reliability: number
  lead_time_days: number
  quality_score: number
  on_time_delivery: number
  orders_ytd: number
}

interface SupplyChainMetric {
  metric: string
  value: number
  target: number
  trend: string
  change: number
}

interface Statistics {
  active_suppliers: number
  avg_lead_time: number
  supply_chain_cost: number
  on_time_delivery: number
  inventory_value: number
  stockout_rate: number
}

export default function SupplyChainAnalyticsPage() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [metrics, setMetrics] = useState<SupplyChainMetric[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [suppliersRes, metricsRes, statsRes] = await Promise.all([
        fetch('/api/supplychainanalytics/suppliers'),
        fetch('/api/supplychainanalytics/metrics'),
        fetch('/api/supplychainanalytics/statistics')
      ])
      if (suppliersRes.ok) setSuppliers(await suppliersRes.json())
      if (metricsRes.ok) setMetrics(await metricsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching supply chain analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Supply Chain Analytics</h1>
          <p className="mt-2 text-gray-600">Phase 257 - Supply chain visibility and performance</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Suppliers</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.active_suppliers}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Lead Time</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.avg_lead_time}d</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">SC Cost</p>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(statistics.supply_chain_cost)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">On-Time</p>
              <p className="text-2xl font-bold text-green-600">{statistics.on_time_delivery}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Inventory</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(statistics.inventory_value)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Stockout Rate</p>
              <p className="text-2xl font-bold text-red-600">{statistics.stockout_rate}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Supplier Performance</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reliability</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quality</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">On-Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orders YTD</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {suppliers.map((supplier) => (
                  <tr key={supplier.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{supplier.name}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{supplier.category}</span>
                    </td>
                    <td className="px-6 py-4 text-green-600 font-bold">{supplier.reliability}%</td>
                    <td className="px-6 py-4 text-gray-500">{supplier.lead_time_days}d</td>
                    <td className="px-6 py-4 text-blue-600">{supplier.quality_score}</td>
                    <td className="px-6 py-4 text-purple-600">{supplier.on_time_delivery}%</td>
                    <td className="px-6 py-4 text-gray-500">{supplier.orders_ytd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Supply Chain Metrics</h2>
          </div>
          <div className="p-6 space-y-4">
            {metrics.map((metric, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{metric.metric}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-lg font-bold ${metric.value >= metric.target ? 'text-green-600' : 'text-yellow-600'}`}>
                      {typeof metric.value === 'number' && metric.value % 1 !== 0 ? metric.value.toFixed(1) : metric.value}
                    </span>
                    <span className={`text-sm ${metric.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {metric.change >= 0 ? '+' : ''}{metric.change}
                    </span>
                  </div>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div className={`h-2 rounded-full ${metric.value >= metric.target ? 'bg-green-500' : 'bg-yellow-500'}`}
                       style={{width: `${Math.min((metric.value / metric.target) * 100, 100)}%`}}></div>
                </div>
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-gray-500">Target: {metric.target}</span>
                  <span className={`${metric.trend === 'up' ? 'text-green-600' : metric.trend === 'down' ? 'text-red-600' : 'text-gray-500'}`}>
                    Trend: {metric.trend}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
